/**
 * EmotionalLightingEngine — derives scene lighting parameters from macro state,
 * mempool pressure, and block events.
 *
 * 3-layer system:
 *   Layer 1: Base light (macro state) — slow, ambient
 *   Layer 2: Pressure light (mempool tension) — subtle modulation
 *   Layer 3: Event light (block found) — brief, warm relief
 *
 * All outputs are CSS-friendly values (colors, opacities, filter strings).
 */

export type MacroLightState =
  | 'DEEP_VALUE'
  | 'ACCUMULATION'
  | 'NEUTRAL'
  | 'WARM'
  | 'HOT'
  | 'MIXED';

export interface LightingInputs {
  macroState: MacroLightState;
  /** 0–1: mempool congestion / waiting pressure */
  mempoolPressure: number;
  /** true during block-found transient */
  blockJustFound: boolean;
  /** 0–1: heat level from buddy engine */
  heatLevel: number;
}

export interface SceneLightingParams {
  // Layer 1: Base ambient
  skyGradient: string;
  horizonGlow: string;
  horizonOpacity: number;
  ambientBrightness: number;
  vignetteIntensity: number;
  /** Color temperature overlay */
  tintColor: string;
  tintOpacity: number;

  // Layer 2: Pressure modulation
  pressureDarken: number; // 0–0.15 extra darkening
  pressureTightness: number; // vignette boost

  // Layer 3: Event light
  eventGlowOpacity: number;
  eventGlowColor: string;

  // Buddy CSS filter
  buddyFilter: string;

  // Hut window glow
  hutGlowOpacity: number;
  hutGlowColor: string;
}

// ── Base light presets by macro state ──

interface BaseLightPreset {
  skyGradient: string;
  horizonGlow: string;
  horizonOpacity: number;
  ambientBrightness: number;
  vignetteIntensity: number;
  tintColor: string;
  tintOpacity: number;
  buddyFilter: string;
  hutGlowOpacity: number;
}

const BASE_PRESETS: Record<MacroLightState, BaseLightPreset> = {
  DEEP_VALUE: {
    skyGradient: 'linear-gradient(180deg, hsla(220, 45%, 12%, 0.55) 0%, hsla(215, 40%, 18%, 0.35) 40%, hsla(210, 35%, 22%, 0.20) 70%, hsla(200, 30%, 25%, 0.10) 100%)',
    horizonGlow: 'radial-gradient(ellipse 90% 35% at 50% 85%, hsla(210, 50%, 40%, 0.12) 0%, transparent 70%)',
    horizonOpacity: 0.7,
    ambientBrightness: 0.82,
    vignetteIntensity: 0.35,
    tintColor: 'hsla(215, 55%, 45%, 0.08)',
    tintOpacity: 0.6,
    buddyFilter: 'brightness(0.92) hue-rotate(-8deg)',
    hutGlowOpacity: 0.6,
  },
  ACCUMULATION: {
    skyGradient: 'linear-gradient(180deg, hsla(210, 35%, 18%, 0.40) 0%, hsla(205, 30%, 25%, 0.25) 40%, hsla(200, 25%, 35%, 0.12) 70%, hsla(195, 20%, 45%, 0.05) 100%)',
    horizonGlow: 'radial-gradient(ellipse 85% 30% at 50% 85%, hsla(200, 40%, 50%, 0.10) 0%, transparent 70%)',
    horizonOpacity: 0.6,
    ambientBrightness: 0.88,
    vignetteIntensity: 0.28,
    tintColor: 'hsla(200, 40%, 50%, 0.05)',
    tintOpacity: 0.4,
    buddyFilter: 'brightness(0.96) hue-rotate(-3deg)',
    hutGlowOpacity: 0.5,
  },
  NEUTRAL: {
    skyGradient: 'linear-gradient(180deg, hsla(220, 15%, 30%, 0.18) 0%, hsla(215, 10%, 40%, 0.10) 40%, hsla(210, 8%, 50%, 0.05) 70%, transparent 100%)',
    horizonGlow: 'radial-gradient(ellipse 80% 25% at 50% 85%, hsla(30, 20%, 60%, 0.06) 0%, transparent 70%)',
    horizonOpacity: 0.4,
    ambientBrightness: 0.95,
    vignetteIntensity: 0.20,
    tintColor: 'hsla(220, 10%, 50%, 0.03)',
    tintOpacity: 0.2,
    buddyFilter: 'brightness(1.0)',
    hutGlowOpacity: 0.35,
  },
  WARM: {
    skyGradient: 'linear-gradient(180deg, hsla(30, 25%, 25%, 0.12) 0%, hsla(25, 30%, 35%, 0.08) 40%, hsla(20, 35%, 50%, 0.04) 70%, transparent 100%)',
    horizonGlow: 'radial-gradient(ellipse 85% 30% at 50% 82%, hsla(30, 55%, 55%, 0.12) 0%, transparent 70%)',
    horizonOpacity: 0.6,
    ambientBrightness: 1.02,
    vignetteIntensity: 0.15,
    tintColor: 'hsla(30, 45%, 55%, 0.05)',
    tintOpacity: 0.35,
    buddyFilter: 'brightness(1.03) hue-rotate(3deg)',
    hutGlowOpacity: 0.25,
  },
  HOT: {
    skyGradient: 'linear-gradient(180deg, hsla(35, 35%, 30%, 0.08) 0%, hsla(30, 40%, 45%, 0.05) 40%, hsla(25, 50%, 55%, 0.03) 70%, transparent 100%)',
    horizonGlow: 'radial-gradient(ellipse 90% 35% at 50% 80%, hsla(35, 65%, 60%, 0.16) 0%, transparent 70%)',
    horizonOpacity: 0.75,
    ambientBrightness: 1.06,
    vignetteIntensity: 0.10,
    tintColor: 'hsla(35, 55%, 55%, 0.06)',
    tintOpacity: 0.4,
    buddyFilter: 'brightness(1.06) hue-rotate(5deg)',
    hutGlowOpacity: 0.15,
  },
  MIXED: {
    skyGradient: 'linear-gradient(180deg, hsla(240, 15%, 22%, 0.25) 0%, hsla(30, 15%, 30%, 0.12) 40%, hsla(200, 10%, 40%, 0.08) 70%, transparent 100%)',
    horizonGlow: 'radial-gradient(ellipse 75% 25% at 55% 85%, hsla(30, 30%, 50%, 0.08) 0%, transparent 60%)',
    horizonOpacity: 0.5,
    ambientBrightness: 0.93,
    vignetteIntensity: 0.25,
    tintColor: 'hsla(260, 15%, 45%, 0.04)',
    tintOpacity: 0.3,
    buddyFilter: 'brightness(0.98)',
    hutGlowOpacity: 0.3,
  },
};

// ── Pressure modulation ──

function computePressure(mempoolPressure: number): { darken: number; tightness: number } {
  const p = Math.max(0, Math.min(1, mempoolPressure));
  return {
    darken: p * 0.10,       // up to 10% extra darkening
    tightness: p * 0.08,    // extra vignette
  };
}

// ── Main derivation ──

export function deriveSceneLighting(inputs: LightingInputs): SceneLightingParams {
  const base = BASE_PRESETS[inputs.macroState] || BASE_PRESETS.NEUTRAL;
  const pressure = computePressure(inputs.mempoolPressure);

  // Event light: warm glow on block found
  const eventGlowOpacity = inputs.blockJustFound ? 0.14 : 0;
  const eventGlowColor = 'hsla(35, 70%, 65%, 0.20)';

  return {
    skyGradient: base.skyGradient,
    horizonGlow: base.horizonGlow,
    horizonOpacity: base.horizonOpacity,
    ambientBrightness: base.ambientBrightness,
    vignetteIntensity: base.vignetteIntensity + pressure.tightness,
    tintColor: base.tintColor,
    tintOpacity: base.tintOpacity,

    pressureDarken: pressure.darken,
    pressureTightness: pressure.tightness,

    eventGlowOpacity,
    eventGlowColor,

    buddyFilter: base.buddyFilter,

    hutGlowOpacity: base.hutGlowOpacity,
    hutGlowColor: inputs.macroState === 'DEEP_VALUE' || inputs.macroState === 'ACCUMULATION'
      ? 'hsla(35, 75%, 55%, 0.40)'
      : 'hsla(35, 60%, 60%, 0.25)',
  };
}
